import moment from 'moment';
import { getBreadcrumbLabel } from './client-store';

type UserInfoType = {
  identityToken: string;
  email: string;
  entityType: string;
  membershipRole?: string;
  membershipId: string;
  name: string;
  entityName: string;
  path: string;
  pathLabel?: string;
  pathUpdatedAt?: string;
  saveLastVisitedPage?: boolean | null;
  color: string;
  logo?: string;
  lastRequestTime?: number; // in milliseconds
  idleTimeoutDuration?: number; // in milliseconds
};

export type AccountInfoType = Omit<UserInfoType, 'identityToken'>;

type AccountInfoFragmentedType = {
  [P in keyof AccountInfoType]?: AccountInfoType[P];
};

class Auth {
  userInfo: UserInfoType | null = null;
  accounts: UserInfoType[] = [];

  constructor() {
    this.load();
    this.initLastRequestTime();
  }

  identityAuthToken() {
    return this.userInfo?.identityToken;
  }

  email() {
    return this.userInfo?.email;
  }

  name() {
    return this.userInfo?.name;
  }

  membershipId() {
    return this.userInfo?.membershipId;
  }

  path() {
    return this.userInfo?.path;
  }

  pathLabel() {
    return this.userInfo?.pathLabel;
  }

  pathUpdatedAt() {
    return this.userInfo?.pathUpdatedAt;
  }

  saveLastVisitedPage() {
    return this.userInfo?.saveLastVisitedPage;
  }

  idleTimeoutDuration() {
    return this.userInfo?.idleTimeoutDuration; // in milliseconds
  }

  lastRequestTime() {
    return this.userInfo?.lastRequestTime || Date.now(); // in milliseconds
  }

  color() {
    return this.userInfo?.color;
  }

  membershipRole() {
    return this.userInfo?.membershipRole;
  }

  membershipType() {
    return this.userInfo?.entityType;
  }

  entityName() {
    return this.userInfo?.entityName;
  }

  logo() {
    return this.userInfo?.logo;
  }

  account(): AccountInfoType | undefined {
    if (!this.userInfo) return;

    const { identityToken, ...rest } = this.userInfo;
    return rest;
  }

  update(data: AccountInfoFragmentedType) {
    if (!this.userInfo) return;
    this.userInfo = { ...this.userInfo, ...data };
    this.store(this.userInfo);
  }

  organizationMember() {
    return this.membershipType() === 'Organization';
  }

  supplierMember() {
    return this.membershipType() === 'Supplier';
  }

  signedIn() {
    return this.userInfo !== null;
  }

  signOut() {
    this.accounts = this.accounts.filter(
      ({ membershipId }) => membershipId !== this.userInfo?.membershipId
    );
    this.userInfo = null;
    this.save();
  }

  store(userInfo: UserInfoType) {
    this.userInfo = userInfo;
    const otherAccounts = this.accounts.filter(
      (user) => userInfo.membershipId !== user.membershipId
    );
    this.accounts = [userInfo, ...otherAccounts];
    this.save();
  }

  allAccounts(): AccountInfoType[] {
    return this.accounts.map(({ identityToken, ...rest }) => rest);
  }

  colorsBeingUsed(): string[] {
    return this.accounts.map(({ color }) => color);
  }

  signOutAllData() {
    return this.accounts.map(({ membershipId }) => ({
      membershipId,
    }));
  }

  removeAccount(membershipId: string) {
    this.accounts = this.accounts.filter(
      (account) => account.membershipId !== membershipId
    );
    this.save();
  }

  switch(membershipId: string, fromPath?: string) {
    if (this.userInfo && fromPath) {
      this.userInfo.path = fromPath;
      this.userInfo.pathLabel = getBreadcrumbLabel();
      this.userInfo.pathUpdatedAt = moment().toISOString();
      this.store(this.userInfo);
    }
    const account = this.findAccount(membershipId);
    if (account) this.store(account);
  }

  load() {
    const info = localStorage.getItem('twUser');
    const accounts = localStorage.getItem('accounts');

    this.accounts = accounts ? JSON.parse(accounts) : [];

    if (!info) {
      this.signOut();
      return;
    }

    this.userInfo = JSON.parse(info);
  }

  initLastRequestTime() {
    this.accounts.forEach((account) => {
      if (!account.lastRequestTime) {
        account.lastRequestTime = Date.now();
      }
    });

    if (this.userInfo && !this.userInfo.lastRequestTime) {
      this.userInfo.lastRequestTime = Date.now();
    }
  }

  save() {
    if (this.userInfo) {
      localStorage.setItem('twUser', JSON.stringify(this.userInfo));
    } else {
      localStorage.removeItem('twUser');
    }
    localStorage.setItem('accounts', JSON.stringify(this.accounts));
  }

  reset() {
    this.userInfo = null;
    this.accounts = [];
    this.save();
  }

  findAccount(membershipId: string) {
    const account = this.accounts.find(
      (account) => account.membershipId === membershipId
    );
    return account;
  }

  isSessionExpired() {
    const idleTimeoutDuration = this.idleTimeoutDuration();
    const lastRequestTime = this.lastRequestTime();

    if (!idleTimeoutDuration || !lastRequestTime) return false;

    const sessionExpireTime = lastRequestTime + idleTimeoutDuration;

    return Date.now() > sessionExpireTime;
  }
}

export default new Auth();
